import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_ITEMS = [
  { id: 'hero',     label: 'Home' },
  { id: 'about',    label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'training', label: 'Training' },
  { id: 'clients',  label: 'Clients' },
  { id: 'products', label: 'Products' },
  { id: 'contact',  label: 'Contact' },
];

export default function Navbar({ activeSection, setActiveSection }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    setOpen(false);
    setActiveSection(id);
    if (id === 'hero') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled
            ? 'shadow-[0_4px_30px_rgba(232,80,10,0.35)]'
            : ''
        }`}
        style={{ background: 'linear-gradient(135deg, #E8500A 0%, #FF6A00 100%)' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-[72px] lg:h-[80px]">

            {/* ── Logo ─────────────────────────────────────────── */}
            <a
              href="#hero"
              onClick={(e) => scrollTo(e, 'hero')}
              className="flex items-center gap-3.5 shrink-0 group"
              aria-label="EazyWorld Home"
            >
              <div className="relative flex items-center justify-center bg-white rounded-xl p-1.5 shadow-sm transition-transform duration-300 group-hover:scale-105">
                <img
                  src="/images/eazyworld_logo.png"
                  alt="EazyWorld Logo"
                  className="h-9 lg:h-[44px] w-auto object-contain"
                />
              </div>

              {/* Divider */}
              <span className="hidden sm:block w-px h-9 bg-white/30 mx-0.5" />

              <div className="hidden sm:block">
                <p className="text-[15px] lg:text-[17px] font-black tracking-tight text-white font-heading leading-none">
                  EAZYWORLD
                </p>
                <p className="text-[9px] lg:text-[10px] font-semibold tracking-[0.22em] text-white/75 uppercase mt-[3px] leading-none">
                  EHS Engineering
                </p>
              </div>
            </a>

            {/* ── Desktop Nav Links ─────────────────────────────── */}
            <ul className="hidden lg:flex items-center gap-0.5">
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => scrollTo(e, item.id)}
                      className="relative px-4 py-2.5 text-[13.5px] font-medium rounded-lg inline-flex items-center transition-all duration-200 group"
                      style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.78)' }}
                    >
                      {/* Hover bg pill */}
                      <span
                        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        style={{ background: 'rgba(255,255,255,0.15)' }}
                      />

                      <span className="relative z-10 group-hover:text-white transition-colors duration-200 font-semibold">
                        {item.label}
                      </span>

                      {/* Active indicator – white underline */}
                      {isActive && (
                        <motion.span
                          layoutId="nav-active-dot"
                          className="absolute bottom-1 left-3 right-3 h-[2px] rounded-full bg-white"
                        />
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>

            {/* ── CTA Button (Desktop) ──────────────────────────── */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+1234567890"
                className="text-[13px] font-medium text-white/70 hover:text-white transition-colors duration-200 hidden xl:block"
              >
                ☎ &nbsp;Call Us
              </a>
              <a
                href="#contact"
                onClick={(e) => scrollTo(e, 'contact')}
                className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-brand-orange text-[13.5px] font-bold tracking-wide overflow-hidden group transition-all duration-300 hover:bg-white/90 hover:-translate-y-0.5 active:translate-y-0 shadow-[0_2px_12px_rgba(0,0,0,0.15)]"
              >
                {/* Shine sweep on hover */}
                <span className="absolute inset-0 translate-x-[-110%] group-hover:translate-x-[110%] transition-transform duration-500 bg-gradient-to-r from-transparent via-orange-100/40 to-transparent skew-x-[-20deg]" />
                <span className="relative">Get in Touch</span>
                <svg className="relative w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>

            {/* ── Hamburger (Mobile) ────────────────────────────── */}
            <button
              className="lg:hidden flex flex-col justify-center items-center w-10 h-10 rounded-lg hover:bg-white/20 transition-colors duration-200 gap-[5px]"
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  animate={
                    open
                      ? i === 0 ? { rotate: 45, y: 7 }
                      : i === 1 ? { opacity: 0, scaleX: 0 }
                      : { rotate: -45, y: -7 }
                      : { rotate: 0, y: 0, opacity: 1, scaleX: 1 }
                  }
                  transition={{ duration: 0.22 }}
                  className="block w-5 h-[1.8px] bg-white rounded-full origin-center"
                />
              ))}
            </button>

          </div>
        </div>
      </motion.nav>

      {/* ── Mobile Drawer ─────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
              onClick={() => setOpen(false)}
            />

            {/* Drawer panel */}
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'tween', duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="fixed top-0 right-0 bottom-0 z-50 w-[280px] bg-white shadow-2xl flex flex-col lg:hidden"
            >
              {/* Drawer header */}
              <div
                className="flex items-center justify-between px-6 h-[72px] border-b border-orange-500/30"
                style={{ background: 'linear-gradient(135deg, #E8500A 0%, #FF6A00 100%)' }}
              >
                <div>
                  <p className="text-[15px] font-black tracking-tight text-white font-heading leading-none">EAZYWORLD</p>
                  <p className="text-[9px] font-semibold tracking-[0.2em] text-white/75 uppercase mt-[3px]">EHS Engineering</p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  aria-label="Close menu"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1l12 12M13 1L1 13" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto px-4 py-4">
                {NAV_ITEMS.map((item, idx) => {
                  const isActive = activeSection === item.id;
                  return (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => scrollTo(e, item.id)}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 + 0.1 }}
                      className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-[15px] font-medium mb-1 transition-colors duration-200 ${
                        isActive
                          ? 'bg-orange-50 text-brand-orange'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      {item.label}
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
                      )}
                    </motion.a>
                  );
                })}
              </nav>

              {/* CTA */}
              <div className="px-6 py-6 border-t border-gray-100">
                <a
                  href="#contact"
                  onClick={(e) => scrollTo(e, 'contact')}
                  className="block w-full text-center py-3 rounded-xl bg-brand-orange text-white font-semibold text-[14px] tracking-wide hover:bg-brand-orange-l transition-all duration-300"
                >
                  Get in Touch
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
